import PubSub from "pubsub-js";
import QuickLRU from "quick-lru";

import type { AppConfiguration, Configuration, License } from "../api/graphql.ts";
import { urls } from "../api/urls.ts";
import { PubSubSharedActions } from "../messaging/pubsub-actions.ts";

export type SelectorConfiguration = AppConfiguration & License;

interface CachedConfig {
  config: SelectorConfiguration;
  timestamp: number;
}

// Define the stale time
const STALE_TIME = 30 * 60 * 1000; // 30 minutes in milliseconds

// The cache will be used to store the application configuration. It has a time-to-live (ttl) of 1 hour (60 * 60 * 1000 milliseconds).
const cache = new QuickLRU<string, CachedConfig>({
  maxSize: 1, // We only need to store one configuration object, so the maximum size of the cache is set to 1.
  maxAge: 60 * 60 * 1000, // Time-to-live (ttl) is set to 1 hour in milliseconds.
});

// Promise to prevent redundant network requests
let fetchingPromise: Promise<SelectorConfiguration | undefined> | undefined;

// Clear cache on logout
PubSub.subscribe(PubSubSharedActions.LOGOUT, () => {
  cache.clear();
});

/**
 * Retrieves the application configuration.
 *
 * - Returns cached configuration if available and not expired.
 * - Fetches new configuration from the server if cache is expired or unavailable.
 * - Prevents redundant network requests with concurrency handling.
 *
 * @returns A promise that resolves to the application configuration or undefined if an error occurs.
 */
export async function getApplicationConfig(): Promise<SelectorConfiguration | undefined> {
  const cachedData = cache.get("config");
  const now = Date.now();

  // Check if cached configuration is available and valid
  if (cachedData) {
    const isStale = now - cachedData.timestamp > STALE_TIME;

    if (isStale) {
      // Data is stale, return it and initiate background fetch
      fetchAndUpdateConfig(); // Start background fetch without awaiting
      return cachedData.config;
    } else {
      // Data is fresh, return it
      return cachedData.config;
    }
  }

  // If a fetch is already in progress, return the existing promise
  if (fetchingPromise != undefined) {
    return fetchingPromise;
  }

  // Start fetching the configuration
  fetchingPromise = fetchAndUpdateConfig();

  return fetchingPromise;
}

/**
 * Fetches the configuration from the server and updates the cache.
 *
 * @returns A promise that resolves to the application configuration or undefined if an error occurs.
 */
async function fetchAndUpdateConfig(): Promise<SelectorConfiguration | undefined> {
  try {
    const response = await fetch(`${urls.api}/info`, {
      method: "GET",
      credentials: "include",
      headers: {
        Accept: "application/json",
      },
    });

    const configuration = (await response.json()) as Configuration;

    // Merge application and license configurations
    const newConfig = configuration
      ? ({ ...configuration.application, ...configuration.license } as SelectorConfiguration)
      : undefined;

    if (newConfig != undefined) {
      cache.set("config", {
        config: newConfig,
        timestamp: Date.now(),
      });
    }

    return newConfig;
  } catch {
    // TODO: Handle error appropriately, possibly show an alert
    return;
  } finally {
    // Reset the fetchingPromise after completion
    fetchingPromise = undefined;
  }
}
